import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import * as XLSX from "xlsx"
import { Toolbar } from "@/components/organisms"
import { ControlBar } from "@/components/organisms/ControlBar"
import { SearchableSelect } from "@/components/molecules/SearchableSelect"
import { ButtonGroup, Input, CardListItem } from "@/components/molecules"
import { Button, Checkbox, Text } from "@/components/atoms"
import { dateMask } from "@/utils/masks"
import {
  getEmpresasSemSessao,
  getSessoesEmpresa,
  getMentoresLista,
  getAuditoria,
  getEmpresasLista,
  getContratosEmpresa,
  type EmpresaSemSessaoItem,
  type SessaoEmpresaItem,
  type AuditoriaAnaliticoItem,
  type AuditoriaSinteticoItem,
  type AuditoriaParams,
} from "@/services/reportService"
import { useToast } from "@/hooks/useToast"
import { useAuth } from "@/contexts/AuthContext"
import { useModal } from "@/components/molecules/Modal/ModalProvider"
import { cn } from "@/utils/cn"

// ─── shared ───────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20
const TODOS_OPTION = { value: "", label: "Todos" }

const breadcrumbItems = [
  { href: "/", label: "Início" },
  { href: "/reports", label: "Relatórios", isCurrent: true },
]

const thClass =
  "px-4 py-3 text-left text-xs font-semibold text-white/50 uppercase tracking-wide whitespace-nowrap"
const tdClass = "px-4 py-3 text-sm text-white/80 whitespace-nowrap"

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })

const formatDateShort = (iso: string) => new Date(iso).toLocaleDateString("pt-BR")

function statusGrupo(status: number | null): { label: string; className: string } {
  if (status === 1) return { label: "Agendada", className: "bg-yellow-500/20 text-yellow-300" }
  if (status === 4) return { label: "No-show", className: "bg-gray-500/20 text-gray-300" }
  if (status === 5) return { label: "Cancelado", className: "bg-red-500/20 text-red-300" }
  return { label: "Realizado", className: "bg-green-500/20 text-green-300" }
}

// ─── acompanhamento types ──────────────────────────────────────────────────────
type FilterMode = "minimo" | "intervalo"
type ViewMode = "analitico" | "sintetico"
type ViewPorMode = "empresa" | "mentor"

type SinteticoAcRow = {
  mentorPrincipal: string
  qtdEmpresas: number
  empresas: EmpresaSemSessaoItem[]
}

function buildSintetico(empresas: EmpresaSemSessaoItem[]): SinteticoAcRow[] {
  const map = new Map<string, EmpresaSemSessaoItem[]>()
  for (const emp of empresas) {
    const key = emp.mentorPrincipal ?? "Sem mentor"
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(emp)
  }
  return Array.from(map.entries())
    .map(([mentor, items]) => ({
      mentorPrincipal: mentor,
      qtdEmpresas: items.length,
      empresas: items,
    }))
    .sort((a, b) => b.qtdEmpresas - a.qtdEmpresas)
}

// ─── auditoria types ───────────────────────────────────────────────────────────
type TipoRelatorio = "analitico" | "sintetico"
type TipoMentor = "ambos" | "principal" | "especialista"

const TIPO_MENTOR_OPTIONS = [
  { value: "ambos", label: "Ambos" },
  { value: "principal", label: "Principal" },
  { value: "especialista", label: "Especialista" },
]

const today = new Date().toISOString().split("T")[0]
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  .toISOString()
  .split("T")[0]

const toISO = (masked: string) => {
  const parts = masked.split("/")
  if (parts.length !== 3 || parts[2].length !== 4) return ""
  return `${parts[2]}-${parts[1]}-${parts[0]}`
}

const toMasked = (iso: string) => {
  if (!iso) return ""
  const [y, m, d] = iso.split("-")
  return `${d}/${m}/${y}`
}

const DEFAULT_DATA_INICIO = toMasked(firstOfMonth)
const DEFAULT_DATA_FIM = toMasked(today)

// ─── main component ────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const showToast = useToast()
  const { user } = useAuth()
  const { showModal, hideModal } = useModal()
  const isAdmin = user?.admin === true

  // ControlBar
  const [isFilterOpened, setIsFilterOpened] = useState(true)
  const [selectedControlTab, setSelectedControlTab] = useState("acompanhamento")

  // ── Acompanhamento state ─────────────────────────────────────────────────────
  const [acFilterMode, setAcFilterMode] = useState<FilterMode>("minimo")
  const [acViewMode, setAcViewMode] = useState<ViewMode>("analitico")
  const [acViewPor, setAcViewPor] = useState<ViewPorMode>("empresa")
  const [acDiasMin, setAcDiasMin] = useState(30)
  const [acDiasMax, setAcDiasMax] = useState(60)
  const [acMentorFiltro, setAcMentorFiltro] = useState("")
  const [acMentores, setAcMentores] = useState<{ value: string; label: string }[]>([TODOS_OPTION])
  const [acEmpresas, setAcEmpresas] = useState<EmpresaSemSessaoItem[]>([])
  const [acLoading, setAcLoading] = useState(false)
  const [acSearched, setAcSearched] = useState(false)
  const [acVisibleCount, setAcVisibleCount] = useState(PAGE_SIZE)
  const [acLastSearchedKey, setAcLastSearchedKey] = useState<string | null>(null)

  const acSentinelRef = useRef<HTMLDivElement>(null)

  // ── Auditoria state ──────────────────────────────────────────────────────────
  const [auDataInicio, setAuDataInicio] = useState(DEFAULT_DATA_INICIO)
  const [auDataFim, setAuDataFim] = useState(DEFAULT_DATA_FIM)
  const [auMentorId, setAuMentorId] = useState<string>(() =>
    isAdmin ? "" : String(user?.mentor_id ?? "")
  )
  const [auEmpId, setAuEmpId] = useState<string>("")
  const [auTipoMentor, setAuTipoMentor] = useState<TipoMentor>("ambos")
  const [auTipoRelatorio, setAuTipoRelatorio] = useState<TipoRelatorio>("analitico")
  const [auMentores, setAuMentores] = useState<{ value: string; label: string }[]>([TODOS_OPTION])
  const [auEmpresasLista, setAuEmpresasLista] = useState<{ value: string; label: string }[]>([
    TODOS_OPTION,
  ])
  const [auResultado, setAuResultado] = useState<
    (AuditoriaAnaliticoItem | AuditoriaSinteticoItem)[]
  >([])
  const [auTotal, setAuTotal] = useState(0)
  const [auLoading, setAuLoading] = useState(false)
  const [auSearched, setAuSearched] = useState(false)
  const [auVisibleCount, setAuVisibleCount] = useState(PAGE_SIZE)
  const [auLastSearchedKey, setAuLastSearchedKey] = useState<string | null>(null)

  const auSentinelRef = useRef<HTMLDivElement>(null)

  // ── Initial loads ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return
    getMentoresLista()
      .then((data) =>
        setAcMentores([
          TODOS_OPTION,
          ...data.map((m) => ({ value: String(m.mentorId), label: m.nome })),
        ])
      )
      .catch(() => {})
  }, [isAdmin])

  useEffect(() => {
    getMentoresLista()
      .then((data) =>
        setAuMentores([
          TODOS_OPTION,
          ...data.map((m) => ({ value: String(m.mentorId), label: m.nome })),
        ])
      )
      .catch(() => {})

    getEmpresasLista(isAdmin ? undefined : (user?.mentor_id ?? undefined))
      .then((data) =>
        setAuEmpresasLista([
          TODOS_OPTION,
          ...data.map((e) => ({
            value: String(e.empId),
            label:
              e.razaoSocial && e.razaoSocial !== e.nomeFantasia
                ? `${e.nomeFantasia} | ${e.razaoSocial}`
                : e.nomeFantasia,
          })),
        ])
      )
      .catch(() => {})
  }, [])

  // ── Infinite scroll observers ────────────────────────────────────────────────
  useEffect(() => {
    const el = acSentinelRef.current
    if (!el || !acSearched) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting)
          setAcVisibleCount((prev) => Math.min(prev + PAGE_SIZE, acEmpresas.length))
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [acSearched, acEmpresas])

  useEffect(() => {
    const el = auSentinelRef.current
    if (!el || !auSearched) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting)
          setAuVisibleCount((prev) => Math.min(prev + PAGE_SIZE, auResultado.length))
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [auSearched, auResultado])

  // ── Acompanhamento handlers ──────────────────────────────────────────────────
  const handleAcBuscar = useCallback(async () => {
    if (!acDiasMin || acDiasMin < 1) {
      showToast({ variant: "error", message: "Informe um número de dias válido." })
      return
    }
    if (acFilterMode === "intervalo" && acDiasMax <= acDiasMin) {
      showToast({
        variant: "error",
        message: "O valor máximo de dias deve ser maior que o mínimo.",
      })
      return
    }
    setAcLoading(true)
    try {
      const mentorParaEnvio =
        isAdmin && acViewPor === "mentor" && acMentorFiltro ? Number(acMentorFiltro) : undefined
      const data = await getEmpresasSemSessao(
        acDiasMin,
        acFilterMode === "intervalo" ? acDiasMax : undefined,
        mentorParaEnvio,
        isAdmin ? acViewPor : undefined
      )
      setAcEmpresas(data)
      setAcSearched(true)
      setAcVisibleCount(PAGE_SIZE)
      setAcLastSearchedKey(`${acFilterMode}|${acDiasMin}|${acDiasMax}|${acViewPor}|${acMentorFiltro}`)
    } catch {
      showToast({ variant: "error", message: "Erro ao buscar dados." })
    } finally {
      setAcLoading(false)
    }
  }, [acFilterMode, acDiasMin, acDiasMax, isAdmin, acViewPor, acMentorFiltro, showToast])

  const handleAcLimpar = () => {
    setAcDiasMin(30)
    setAcDiasMax(60)
    setAcFilterMode("minimo")
    setAcViewMode("analitico")
    setAcViewPor("empresa")
    setAcMentorFiltro("")
    setAcEmpresas([])
    setAcSearched(false)
    setAcVisibleCount(PAGE_SIZE)
    setAcLastSearchedKey(null)
  }

  // ── Auditoria handlers ───────────────────────────────────────────────────────
  const handleAuBuscar = useCallback(async () => {
    const isoInicio = toISO(auDataInicio)
    const isoFim = toISO(auDataFim)
    if (!isoInicio || !isoFim) {
      showToast({ variant: "error", message: "Data Inicial e Data Final são obrigatórias." })
      return
    }
    if (isoInicio > isoFim) {
      showToast({ variant: "error", message: "Data Inicial não pode ser maior que a Data Final." })
      return
    }
    setAuLoading(true)
    try {
      const params: AuditoriaParams = {
        dataInicio: isoInicio,
        dataFim: isoFim,
        mentorId: auMentorId ? Number(auMentorId) : null,
        empId: auEmpId ? Number(auEmpId) : null,
        tipoMentor: auTipoMentor,
        tipoRelatorio: auTipoRelatorio,
      }
      const res = await getAuditoria(params)
      setAuResultado(res.data)
      setAuTotal(res.meta.total)
      setAuSearched(true)
      setAuVisibleCount(PAGE_SIZE)
      setAuLastSearchedKey(`${auDataInicio}|${auDataFim}|${auMentorId}|${auEmpId}|${auTipoMentor}|${auTipoRelatorio}`)
    } catch {
      showToast({ variant: "error", message: "Erro ao buscar dados de auditoria." })
    } finally {
      setAuLoading(false)
    }
  }, [auDataInicio, auDataFim, auMentorId, auEmpId, auTipoMentor, auTipoRelatorio, showToast])

  const handleAuLimpar = () => {
    setAuDataInicio(DEFAULT_DATA_INICIO)
    setAuDataFim(DEFAULT_DATA_FIM)
    if (isAdmin) setAuMentorId("")
    setAuEmpId("")
    setAuTipoMentor("ambos")
    setAuTipoRelatorio("analitico")
    setAuResultado([])
    setAuTotal(0)
    setAuSearched(false)
    setAuVisibleCount(PAGE_SIZE)
    setAuLastSearchedKey(null)
  }


  const handleAuVerContratos = async () => {
    const contratos = await getContratosEmpresa(Number(auEmpId)).catch(() => [])
    const empresaNome = auEmpresasLista.find((e) => e.value === auEmpId)?.label ?? "Empresa"
    const applyDates = (di: string, df: string) => {
      setAuDataInicio(toMasked(di.split("T")[0]))
      setAuDataFim(toMasked(df.split("T")[0]))
      hideModal()
    }
    showModal({
      title: `Contratos — ${empresaNome}`,
      size: "md",
      content: (
        <ContratosModal
          contratos={contratos}
          onUsar={applyDates}
        />
      ),
    })
  }

  // ── ControlBar ───────────────────────────────────────────────────────────────
  const handleTabChange = (tabId: string) => {
    if (tabId === selectedControlTab) {
      setIsFilterOpened((prev) => !prev)
    } else {
      setSelectedControlTab(tabId)
      setIsFilterOpened(true)
    }
  }

  const acAppliedFiltersCount = useMemo(() => {
    let count = 0
    if (acFilterMode !== "minimo") count++
    if (acDiasMin !== 30) count++
    if (acFilterMode === "intervalo" && acDiasMax !== 60) count++
    if (isAdmin && acViewPor !== "empresa") count++
    if (acMentorFiltro) count++
    return count
  }, [acFilterMode, acDiasMin, acDiasMax, isAdmin, acViewPor, acMentorFiltro])

  const auAppliedFiltersCount = useMemo(() => {
    let count = 0
    if (auDataInicio !== DEFAULT_DATA_INICIO) count++
    if (auDataFim !== DEFAULT_DATA_FIM) count++
    if (auMentorId) count++
    if (auEmpId) count++
    if (auTipoMentor !== "ambos") count++
    if (auTipoRelatorio !== "analitico") count++
    return count
  }, [auDataInicio, auDataFim, auMentorId, auEmpId, auTipoMentor, auTipoRelatorio])

  const isAcTab = selectedControlTab === "acompanhamento"
  const currentSearched = isAcTab ? acSearched : auSearched
  const currentAppliedFilters = isAcTab ? acAppliedFiltersCount : auAppliedFiltersCount
  const handleClearCurrent = isAcTab ? handleAcLimpar : handleAuLimpar

  const acDirty =
    acSearched &&
    `${acFilterMode}|${acDiasMin}|${acDiasMax}|${acViewPor}|${acMentorFiltro}` !== acLastSearchedKey
  const auDirty =
    auSearched &&
    `${auDataInicio}|${auDataFim}|${auMentorId}|${auEmpId}|${auTipoMentor}|${auTipoRelatorio}` !==
      auLastSearchedKey

  // ── Acompanhamento derived ───────────────────────────────────────────────────
  const acMentorSelecionado = acMentores.find((m) => m.value === acMentorFiltro)
  const acVerPorMentor = isAdmin && acViewPor === "mentor"
  const acMentorColunaLabel =
    isAdmin && acMentorFiltro ? (acMentorSelecionado?.label ?? "Mentor") : "Mentor"
  const acVisibleEmpresas = acEmpresas.slice(0, acVisibleCount)
  const acHasMore = acVisibleCount < acEmpresas.length
  const acSinteticoRows = buildSintetico(acEmpresas)
  const acEmpresasUnicas = acVerPorMentor
    ? new Set(acEmpresas.map((e) => e.empId)).size
    : acEmpresas.length
  const acResultTitle =
    acFilterMode === "minimo"
      ? `${acVerPorMentor ? "Vínculos" : "Empresas"} sem sessão há mais de ${acDiasMin} dias`
      : `${acVerPorMentor ? "Vínculos" : "Empresas"} sem sessão entre ${acDiasMin} e ${acDiasMax} dias`

  // ── Auditoria derived ────────────────────────────────────────────────────────
  const auIsAnalitico = auTipoRelatorio === "analitico"
  const auVisibleResultado = auResultado.slice(0, auVisibleCount)
  const auHasMore = auVisibleCount < auResultado.length

  const auExportRows = useMemo(() => {
    if (auIsAnalitico) {
      return (auResultado as AuditoriaAnaliticoItem[]).map((row) => ({
        "Data/Hora": formatDate(row.dataHora),
        Mentor: row.mentorNome,
        Empresa: row.empresaNome,
        CNPJ: row.cnpj ?? "",
        "Razão Social": row.razaoSocial ?? "",
        "Tipo Mentor": row.tipoMentor,
        "Tipo Sessão": row.tipoSessao,
        Status: statusGrupo(row.status).label,
      }))
    }
    return (auResultado as AuditoriaSinteticoItem[]).map((row) => ({
      Mentor: row.mentorNome,
      Empresa: row.empresaNome,
      CNPJ: row.cnpj ?? "",
      "Razão Social": row.razaoSocial ?? "",
      Tipo: row.tipoMentor,
      Status: row.statusGrupo,
      Online: row.sessoesOnline,
      Presencial: row.sessoesPresenciais,
      Total: row.totalSessoes,
    }))
  }, [auResultado, auIsAnalitico])

  const handleAcExport = () => {
    const rows = acEmpresas.map((emp) => {
      const nomeMentor = emp.mentorNomeAlvo ?? emp.mentorPrincipal ?? ""
      const papelMentor = emp.papelMentorAlvo ?? "Principal"
      const ultimaPrincipal = emp.ultimaSessaoMentor ?? emp.ultimaSessaoValida ?? null
      const diasPrincipal = emp.diasSemSessaoMentor ?? emp.diasSemSessao ?? 9999
      return {
        Empresa: emp.nomeFantasia,
        Classificação: emp.classificacao ?? "",
        Mentor: nomeMentor,
        Papel: papelMentor,
        "Contrato Início": emp.contratoInicio ? formatDateShort(emp.contratoInicio) : "",
        "Contrato Fim": emp.contratoFim ? formatDateShort(emp.contratoFim) : "",
        [acVerPorMentor ? `Última Sessão (${acMentorColunaLabel})` : "Última Sessão"]:
          ultimaPrincipal ? formatDate(ultimaPrincipal) : "Nunca",
        [acVerPorMentor ? `Dias sem sessão (${acMentorColunaLabel})` : "Dias sem sessão"]:
          diasPrincipal >= 9999 ? "" : diasPrincipal,
      }
    })
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Acompanhamento")
    XLSX.writeFile(wb, `acompanhamento-clientes-${acDiasMin}dias.xlsx`)
  }

  const handleAcExportSintetico = () => {
    const rows = acSinteticoRows.map((row) => ({
      "Mentor Principal": row.mentorPrincipal,
      "Qtd. Empresas": row.qtdEmpresas,
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Sintético")
    XLSX.writeFile(wb, `acompanhamento-sintetico-${acDiasMin}dias.xlsx`)
  }

  const handleAuExport = () => {
    const ws = XLSX.utils.json_to_sheet(auExportRows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, auIsAnalitico ? "Analítico" : "Sintético")
    XLSX.writeFile(
      wb,
      `auditoria-${auIsAnalitico ? "analitico" : "sintetico"}-${toISO(auDataInicio)}-${toISO(auDataFim)}.xlsx`
    )
  }

  const handleAcRowClick = (empresa: EmpresaSemSessaoItem) => {
    showModal({
      title: `Sessões — ${empresa.nomeFantasia}`,
      size: "xl",
      content: <SessoesModal empId={empresa.empId} />,
    })
  }

  const handleAcMentorRowClick = (row: SinteticoAcRow) => {
    showModal({
      title: `Empresas — ${row.mentorPrincipal}`,
      size: "xl",
      content: <MentorEmpresasModal empresas={row.empresas} />,
    })
  }

  return (
    <div className="flex h-full flex-col">
      <Toolbar breadcrumbItems={breadcrumbItems} />

      <div className="flex-1 space-y-6 overflow-auto p-6">
        <div className="pt-24">
          <ControlBar
            isControlBarOpened={isFilterOpened}
            currentTab={selectedControlTab}
            onTabChange={handleTabChange}
            showClearFilters={currentSearched || currentAppliedFilters > 0}
            onClearFilters={handleClearCurrent}
            height={isAcTab ? 220 : 195}
            tabs={[
              {
                id: "acompanhamento",
                label: "Acompanhamento",
                iconName: "chart-histogram",
                counter: acAppliedFiltersCount,
                isActiveOrApplied: acAppliedFiltersCount > 0,
              },
              {
                id: "auditoria",
                label: "Auditoria",
                iconName: "search-alt",
                counter: auAppliedFiltersCount,
                isActiveOrApplied: auAppliedFiltersCount > 0,
              },
            ]}
          >
            {isAcTab ? (
              // ── Acompanhamento filters ─────────────────────────────────────
              <div className="flex flex-col gap-4">
                <DayStepper
                  id="ac-dias-min"
                  label="Mínimo de dias sem sessão"
                  value={acDiasMin}
                  onChange={(v) => setAcDiasMin(v)}
                />

                <div className="flex flex-wrap items-start gap-8">
                  <div className="flex gap-8">
                    <div className="flex flex-col gap-3">
                      <Text variant="label">Tipo de filtro</Text>
                      <CheckboxGroup
                        id="ac-tipo-minimo"
                        label="Mínimo"
                        checked={acFilterMode === "minimo"}
                        onChange={(v) => v && setAcFilterMode("minimo")}
                      />
                      <CheckboxGroup
                        id="ac-tipo-intervalo"
                        label="Intervalo"
                        checked={acFilterMode === "intervalo"}
                        onChange={(v) => v && setAcFilterMode("intervalo")}
                      />
                    </div>

                    {isAdmin && (
                      <div className="flex flex-col gap-3">
                        <Text variant="label">Ver por</Text>
                        <CheckboxGroup
                          id="ac-ver-empresa"
                          label="Empresa"
                          checked={acViewPor === "empresa"}
                          onChange={(v) => v && setAcViewPor("empresa")}
                        />
                        <CheckboxGroup
                          id="ac-ver-mentor"
                          label="Mentor"
                          checked={acViewPor === "mentor"}
                          onChange={(v) => v && setAcViewPor("mentor")}
                        />
                      </div>
                    )}

                    {isAdmin && (
                      <div className="flex flex-col gap-3">
                        <Text variant="label">Visualização</Text>
                        <CheckboxGroup
                          id="ac-vis-analitico"
                          label="Analítico"
                          checked={acViewMode === "analitico"}
                          onChange={(v) => v && setAcViewMode("analitico")}
                        />
                        <CheckboxGroup
                          id="ac-vis-sintetico"
                          label="Sintético"
                          checked={acViewMode === "sintetico"}
                          onChange={(v) => v && setAcViewMode("sintetico")}
                        />
                      </div>
                    )}
                  </div>

                  {isAdmin && acViewPor === "mentor" && (
                    <SearchableSelect
                      id="ac-mentor-filtro"
                      label="Mentor"
                      placeholder="Todos"
                      value={acMentorFiltro}
                      onValueChange={setAcMentorFiltro}
                      options={acMentores}
                    />
                  )}
                </div>
              </div>
            ) : (
              // ── Auditoria filters ──────────────────────────────────────────
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Input
                    id="au-data-inicio"
                    label="Data Inicial *"
                    placeholder="dd/mm/aaaa"
                    value={auDataInicio}
                    onChange={(e) => setAuDataInicio(dateMask(e.target.value))}
                  />
                  <Input
                    id="au-data-fim"
                    label="Data Final *"
                    placeholder="dd/mm/aaaa"
                    value={auDataFim}
                    onChange={(e) => setAuDataFim(dateMask(e.target.value))}
                  />
                  {isAdmin && (
                    <SearchableSelect
                      id="au-mentor-select"
                      label="Mentor"
                      placeholder="Todos"
                      value={auMentorId}
                      onValueChange={setAuMentorId}
                      options={auMentores}
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <SearchableSelect
                      id="au-empresa-select"
                      label="Empresa"
                      placeholder="Todos"
                      value={auEmpId}
                      onValueChange={setAuEmpId}
                      options={auEmpresasLista}
                    />
                    {auEmpId && (
                      <button
                        type="button"
                        onClick={handleAuVerContratos}
                        className="self-start px-1 text-xs text-primary/80 underline-offset-2 transition-colors hover:text-primary hover:underline"
                      >
                        Ver contratos
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex flex-col gap-1">
                    <Text
                      variant="label"
                      className="pl-1"
                    >
                      Tipo de Mentor
                    </Text>
                    <ButtonGroup
                      items={TIPO_MENTOR_OPTIONS}
                      value={auTipoMentor}
                      mandatory
                      onSelectionChange={(v) => setAuTipoMentor(v as TipoMentor)}
                    />
                  </div>
                  <div className="ml-auto flex flex-col gap-1">
                    <Text
                      variant="label"
                      className="pl-1"
                    >
                      Tipo de Relatório
                    </Text>
                    <ButtonGroup
                      items={[
                        { value: "analitico", label: "Analítico" },
                        { value: "sintetico", label: "Sintético" },
                      ]}
                      value={auTipoRelatorio}
                      mandatory
                      onSelectionChange={(v) => setAuTipoRelatorio(v as TipoRelatorio)}
                    />
                  </div>
                </div>
              </div>
            )}
          </ControlBar>
        </div>

        {/* ── Acompanhamento result ─────────────────────────────────────────── */}
        {isAcTab &&
          (!acSearched ? (
            <div className="flex flex-col items-center justify-center gap-5 py-20 text-center">
              <p className="text-sm text-white/50">
                Configure os filtros acima e clique em{" "}
                <strong className="text-white/70">Buscar</strong> para ver os resultados.
              </p>
              <Button
                onClick={handleAcBuscar}
                disabled={acLoading}
              >
                {acLoading ? "Buscando..." : "Buscar"}
              </Button>
            </div>
          ) : (
            <section className="flex flex-col gap-3">
              <div className="flex items-center justify-between px-1 py-2">
                <h2 className="text-sm font-semibold text-white/80">
                  {acResultTitle}
                  <span className="ml-2 font-normal text-white/40">
                    {acVerPorMentor
                      ? `(${acEmpresas.length} vínculo${acEmpresas.length !== 1 ? "s" : ""} · ${acEmpresasUnicas} empresa${acEmpresasUnicas !== 1 ? "s" : ""})`
                      : `(${acEmpresas.length} empresa${acEmpresas.length !== 1 ? "s" : ""})`}
                  </span>
                </h2>
                <div className="flex items-center gap-2">
                  {acDirty && (
                    <Button
                      size="sm"
                      onClick={handleAcBuscar}
                      disabled={acLoading}
                    >
                      {acLoading ? "Atualizando..." : "Atualizar"}
                    </Button>
                  )}
                  {acEmpresas.length > 0 && acViewMode === "analitico" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleAcExport}
                    >
                      Exportar XLSX
                    </Button>
                  )}
                  {acEmpresas.length > 0 && acViewMode === "sintetico" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleAcExportSintetico}
                    >
                      Exportar XLSX
                    </Button>
                  )}
                </div>
              </div>

              {acEmpresas.length === 0 ? (
                <p className="py-8 text-center text-sm text-white/40">
                  Nenhuma empresa encontrada para os filtros selecionados.
                </p>
              ) : acViewMode === "sintetico" ? (
                <>
                  {acSinteticoRows.map((row) => (
                    <div
                      key={row.mentorPrincipal}
                      className="cursor-pointer"
                      onClick={() => handleAcMentorRowClick(row)}
                    >
                      <CardListItem cols={2}>
                        <span className="text-sm font-medium text-white/90">
                          {row.mentorPrincipal}
                        </span>
                        <span className="text-right text-sm font-bold tabular-nums text-white/70">
                          {row.qtdEmpresas} empresa{row.qtdEmpresas !== 1 ? "s" : ""}
                        </span>
                      </CardListItem>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {acVisibleEmpresas.map((emp) => {
                    const contrato =
                      emp.contratoInicio && emp.contratoFim
                        ? `${formatDateShort(emp.contratoInicio)} – ${formatDateShort(emp.contratoFim)}`
                        : "—"
                    const nomeMentor = emp.mentorNomeAlvo ?? emp.mentorPrincipal ?? "—"
                    const papelMentor = emp.papelMentorAlvo ?? "Principal"
                    const ultimaPrincipal = emp.ultimaSessaoMentor ?? emp.ultimaSessaoValida ?? null
                    const diasPrincipal = emp.diasSemSessaoMentor ?? emp.diasSemSessao ?? 9999
                    return (
                      <div
                        key={`${emp.empId}-${emp.mentorIdAlvo ?? "empresa"}`}
                        className="cursor-pointer"
                        onClick={() => handleAcRowClick(emp)}
                      >
                        <CardListItem cols={5}>
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-white/90">
                              {emp.nomeFantasia}
                            </span>
                            <span
                              className={cn(
                                "inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium",
                                emp.classificacao?.toLowerCase().includes("giants")
                                  ? "bg-amber-500/20 text-amber-300"
                                  : "bg-blue-500/20 text-blue-300"
                              )}
                            >
                              {emp.classificacao ?? "—"}
                            </span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-sm text-white/80">{nomeMentor}</span>
                            <span
                              className={cn(
                                "inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium",
                                papelMentor === "Principal"
                                  ? "bg-blue-500/20 text-blue-300"
                                  : "bg-purple-500/20 text-purple-300"
                              )}
                            >
                              {papelMentor}
                            </span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-white/40">Contrato</span>
                            <span className="text-sm text-white/70">{contrato}</span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-white/40">
                              {acVerPorMentor
                                ? `Última sessão (${acMentorColunaLabel})`
                                : "Última sessão"}
                            </span>
                            <span className="text-sm text-white/80">
                              {ultimaPrincipal ? formatDate(ultimaPrincipal) : "Nunca"}
                            </span>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs text-white/40">
                              {acVerPorMentor ? `Dias (${acMentorColunaLabel})` : "Dias sem sessão"}
                            </span>
                            <span className="text-lg font-bold tabular-nums text-white/90">
                              {diasPrincipal >= 9999 ? "—" : diasPrincipal}
                            </span>
                          </div>
                        </CardListItem>
                      </div>
                    )
                  })}
                </>
              )}

              <div
                ref={acSentinelRef}
                className="py-4 text-center text-sm text-white/30"
              >
                {acHasMore ? "Carregando..." : "Fim da listagem"}
              </div>
            </section>
          ))}

        {/* ── Auditoria result ──────────────────────────────────────────────── */}
        {!isAcTab &&
          (!auSearched ? (
            <div className="flex flex-col items-center justify-center gap-5 py-20 text-center">
              <p className="text-sm text-white/50">
                Configure os filtros acima e clique em{" "}
                <strong className="text-white/70">Buscar</strong> para ver os resultados.
              </p>
              <Button
                onClick={handleAuBuscar}
                disabled={auLoading}
              >
                {auLoading ? "Buscando..." : "Buscar"}
              </Button>
            </div>
          ) : (
            <section className="flex flex-col gap-3">
              <div className="flex items-center justify-between px-1 py-2">
                <h2 className="text-sm font-semibold text-white/80">
                  {auIsAnalitico ? "Sessões" : "Resumo por Mentor / Empresa"}
                  <span className="ml-2 font-normal text-white/40">
                    ({auTotal} registro{auTotal !== 1 ? "s" : ""})
                  </span>
                </h2>
                <div className="flex items-center gap-2">
                  {auDirty && (
                    <Button
                      size="sm"
                      onClick={handleAuBuscar}
                      disabled={auLoading}
                    >
                      {auLoading ? "Atualizando..." : "Atualizar"}
                    </Button>
                  )}
                  {auResultado.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleAuExport}
                    >
                      Exportar XLSX
                    </Button>
                  )}
                </div>
              </div>

              {auResultado.length === 0 ? (
                <p className="py-8 text-center text-sm text-white/40">
                  Nenhum registro encontrado para os filtros selecionados.
                </p>
              ) : auIsAnalitico ? (
                <>
                  {(auVisibleResultado as AuditoriaAnaliticoItem[]).map((row) => {
                    const { label: statusLabel, className: statusClass } = statusGrupo(row.status)
                    return (
                      <CardListItem
                        key={row.sessaoId}
                        cols={6}
                      >
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-white/40">Data/Hora</span>
                          <span className="text-sm text-white/90">{formatDate(row.dataHora)}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-white/40">Mentor</span>
                          <span className="text-sm text-white/80">{row.mentorNome}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-white/40">Empresa</span>
                          <span className="text-sm text-white/80">{row.empresaNome}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-white/40">CNPJ / Razão Social</span>
                          <span className="text-sm text-white/70">{row.cnpj ?? "—"}</span>
                          {row.razaoSocial && (
                            <span className="text-xs text-white/40">{row.razaoSocial}</span>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <span
                            className={cn(
                              "inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium",
                              row.tipoMentor === "Principal"
                                ? "bg-blue-500/20 text-blue-300"
                                : "bg-purple-500/20 text-purple-300"
                            )}
                          >
                            {row.tipoMentor}
                          </span>
                          <span className="text-sm text-white/60">{row.tipoSessao}</span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span
                            className={cn(
                              "inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium",
                              statusClass
                            )}
                          >
                            {statusLabel}
                          </span>
                        </div>
                      </CardListItem>
                    )
                  })}
                </>
              ) : (
                <>
                  {(auVisibleResultado as AuditoriaSinteticoItem[]).map((row, i) => (
                    <CardListItem
                      key={`${row.mentorId}-${row.empId}-${row.tipoMentor}-${row.statusGrupo}-${i}`}
                      cols={5}
                    >
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-white/40">Mentor</span>
                        <span className="text-sm font-medium text-white/90">{row.mentorNome}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-white/80">{row.empresaNome}</span>
                        {row.cnpj && <span className="text-xs text-white/40">{row.cnpj}</span>}
                        {row.razaoSocial && (
                          <span className="text-xs text-white/40">{row.razaoSocial}</span>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <span
                          className={cn(
                            "inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium",
                            row.tipoMentor === "Principal"
                              ? "bg-blue-500/20 text-blue-300"
                              : "bg-purple-500/20 text-purple-300"
                          )}
                        >
                          {row.tipoMentor}
                        </span>
                        <span className="text-sm text-white/60">{row.statusGrupo}</span>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white/40">Online</span>
                          <span className="text-sm font-medium tabular-nums text-white/80">
                            {row.sessoesOnline}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white/40">Presencial</span>
                          <span className="text-sm font-medium tabular-nums text-white/80">
                            {row.sessoesPresenciais}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-white/40">Total</span>
                        <span className="text-2xl font-bold tabular-nums text-white/90">
                          {row.totalSessoes}
                        </span>
                      </div>
                    </CardListItem>
                  ))}
                </>
              )}

              <div
                ref={auSentinelRef}
                className="py-4 text-center text-sm text-white/30"
              >
                {auHasMore ? "Carregando..." : "Fim da listagem"}
              </div>
            </section>
          ))}
      </div>
    </div>
  )
}

// ─── sub-components ────────────────────────────────────────────────────────────
const DayStepper = ({
  id,
  label,
  value,
  onChange,
  error,
}: {
  id: string
  label: string
  value: number
  onChange: (v: number) => void
  error?: boolean
}) => (
  <div>
    <Text variant="label">{label}</Text>
    <div className="flex items-center gap-2">
      <Button
        type="button"
        icon="minus-small"
        iconOnly
        size="sm"
        variant="outline"
        onClick={() => onChange(Math.max(1, value - 1))}
      />
      <div className="w-20">
        <Input
          id={id}
          value={String(value)}
          onChange={(e) => onChange(Math.max(1, Number(e.target.value) || 1))}
          state={error ? "error" : "default"}
          className="w-full bg-transparent text-center outline-none"
        />
      </div>
      <Button
        type="button"
        icon="plus-small"
        iconOnly
        size="sm"
        variant="outline"
        onClick={() => onChange(value + 1)}
      />
    </div>
  </div>
)

const CheckboxGroup = ({
  id,
  label,
  checked,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) => (
  <div className="flex items-center gap-2">
    <Checkbox
      id={id}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <Text
      variant="label"
      as="label"
      htmlFor={id}
    >
      {label}
    </Text>
  </div>
)

function SessoesModal({ empId }: { empId: number }) {
  const showToast = useToast()
  const [dataInicio, setDataInicio] = useState("")
  const [dataFim, setDataFim] = useState("")
  const [sessoes, setSessoes] = useState<SessaoEmpresaItem[]>([])
  const [loading, setLoading] = useState(true)

  const fetchSessoes = useCallback(
    async (inicio?: string, fim?: string) => {
      setLoading(true)
      try {
        const data = await getSessoesEmpresa(empId, inicio || undefined, fim || undefined)
        setSessoes(data)
      } catch {
        showToast({ variant: "error", message: "Erro ao carregar sessões." })
      } finally {
        setLoading(false)
      }
    },
    [empId, showToast]
  )

  useEffect(() => {
    fetchSessoes()
  }, [fetchSessoes])

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 border-b border-white/10 pb-3">
        <div className="w-44">
          <Input
            id="sessoes-data-inicio"
            label="Data Inicial"
            placeholder="dd/mm/aaaa"
            variant="outline"
            value={dataInicio}
            onChange={(e) => setDataInicio(dateMask(e.target.value))}
          />
        </div>
        <div className="w-44">
          <Input
            id="sessoes-data-fim"
            label="Data Final"
            placeholder="dd/mm/aaaa"
            variant="outline"
            value={dataFim}
            onChange={(e) => setDataFim(dateMask(e.target.value))}
          />
        </div>
        <Button
          size="sm"
          onClick={() => fetchSessoes(toISO(dataInicio) || undefined, toISO(dataFim) || undefined)}
          disabled={loading}
        >
          Filtrar
        </Button>
      </div>
      {loading ? (
        <p className="py-6 text-center text-sm text-white/40">Carregando...</p>
      ) : sessoes.length === 0 ? (
        <p className="py-6 text-center text-sm text-white/40">Nenhuma sessão encontrada.</p>
      ) : (
        <div className="flex max-h-96 flex-col gap-2 overflow-auto">
          {sessoes.map((s) => {
            const { label, className } = statusGrupo(s.status)
            return (
              <CardListItem key={s.sessaoId} cols={4}>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-white/40">Data/Hora</span>
                  <span className="text-sm text-white/90">{formatDate(s.dataHora)}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-white/40">Mentor</span>
                  <span className="text-sm text-white/80">{s.mentorNome}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-white/40">Tipo</span>
                  <span
                    className={cn(
                      "inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium",
                      s.tipoMentor === "Principal"
                        ? "bg-blue-500/20 text-blue-300"
                        : s.tipoMentor === "Especialista"
                          ? "bg-purple-500/20 text-purple-300"
                          : "bg-gray-500/20 text-gray-300"
                    )}
                  >
                    {s.tipoMentor}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-white/40">Status</span>
                  <span
                    className={cn(
                      "inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium",
                      className
                    )}
                  >
                    {label}
                  </span>
                </div>
              </CardListItem>
            )
          })}
        </div>
      )}
    </div>
  )
}

function MentorEmpresasModal({ empresas }: { empresas: EmpresaSemSessaoItem[] }) {
  return (
    <div className="max-h-[70vh] overflow-auto">
      <table className="w-full min-w-[700px] text-sm">
        <thead className="border-b border-white/10">
          <tr>
            <th className={thClass}>Empresa</th>
            <th className={thClass}>Classificação</th>
            <th className={thClass}>Contrato</th>
            <th className={thClass}>Última Sessão</th>
            <th className={cn(thClass, "text-right")}>Dias sem sessão</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {empresas.map((emp) => {
            const contrato =
              emp.contratoInicio && emp.contratoFim
                ? `${formatDateShort(emp.contratoInicio)} – ${formatDateShort(emp.contratoFim)}`
                : "—"
            const ultima = emp.ultimaSessaoMentor ?? emp.ultimaSessaoValida ?? null
            const dias = emp.diasSemSessaoMentor ?? emp.diasSemSessao ?? 9999
            return (
              <tr
                key={`${emp.empId}-${emp.mentorIdAlvo ?? "empresa"}`}
                className="transition-colors hover:bg-white/5"
              >
                <td className={tdClass}>{emp.nomeFantasia}</td>
                <td className={tdClass}>
                  <span
                    className={cn(
                      "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                      emp.classificacao?.toLowerCase().includes("giants")
                        ? "bg-amber-500/20 text-amber-300"
                        : "bg-blue-500/20 text-blue-300"
                    )}
                  >
                    {emp.classificacao ?? "—"}
                  </span>
                </td>
                <td className={cn(tdClass, "text-white/50")}>{contrato}</td>
                <td className={tdClass}>{ultima ? formatDate(ultima) : "Nunca"}</td>
                <td className={cn(tdClass, "text-right font-bold tabular-nums")}>
                  {dias >= 9999 ? "—" : dias}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ContratosModal({
  contratos,
  onUsar,
}: {
  contratos: { emprId: number; dataInicio: string; dataFim: string }[]
  onUsar: (dataInicio: string, dataFim: string) => void
}) {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString("pt-BR")
  if (contratos.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-white/40">
        Nenhum contrato encontrado para esta empresa.
      </p>
    )
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-white/10">
          <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-white/50">
            Início
          </th>
          <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-white/50">Fim</th>
          <th className="px-3 py-2" />
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">
        {contratos.map((c) => (
          <tr
            key={c.emprId}
            className="transition-colors hover:bg-white/5"
          >
            <td className="px-3 py-2 text-white/80">{fmt(c.dataInicio)}</td>
            <td className="px-3 py-2 text-white/80">{fmt(c.dataFim)}</td>
            <td className="px-3 py-2 text-right">
              <button
                type="button"
                onClick={() => onUsar(c.dataInicio, c.dataFim)}
                className="text-xs text-primary/80 underline-offset-2 transition-colors hover:text-primary hover:underline"
              >
                Usar no filtro
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
